import React from 'react'
import { useTranslation } from 'lib/i18n'
import { connectHierarchicalMenu } from 'react-instantsearch-dom'

import Hit from 'components/Filters/types/Hit'
import FilterTitle from 'components/UI/Filters/FilterTitle'
import FilterWrapper from 'components/UI/Filters/FilterWrapper'

import CustomBreadcrumb from './CustomBreadcrumb'
import MenuItem from './MenuItem'

import styles from './CustomHierarchicalMenu.module.css'

type WrapperProps = {
  attributes: string[]
  wrap?: boolean
  children: JSX.Element
}

const Wrapper = ({
  attributes,
  wrap = true,
  children,
}: WrapperProps): JSX.Element => {
  const { t } = useTranslation()

  if (!wrap) {
    return children
  }

  return (
    <FilterWrapper>
      <FilterTitle title={t('CategoryFilter.header', 'Categories')} />

      <div className={styles.breadcrumb}>
        <CustomBreadcrumb attributes={attributes} />
      </div>

      <div className={styles.menuWrapper}>{children}</div>
    </FilterWrapper>
  )
}

// Type missing in Algolia types
type HierarchicalMenuProvided = {
  items: Array<Hit>
  refine: (value: string) => void
  currentRefinement: string
  attributes: string[]
  wrap?: boolean
}

const CustomHierarchicalMenu = ({
  items,
  refine,
  currentRefinement,
  attributes,
  wrap,
}: HierarchicalMenuProvided): JSX.Element => {
  const byRelated = ({ label, value }: Hit) => {
    if (!currentRefinement) {
      return true
    }

    const itemLevel = (value as string)?.split(' > ').length

    const isSelfOrParent = currentRefinement.includes(label)
    const isChildren = itemLevel > currentRefinement.split('>').length
    const isFromLastLevel = itemLevel === attributes.length

    return isSelfOrParent || isChildren || isFromLastLevel
  }

  const itemsToDisplay = items.filter(byRelated)

  if (!itemsToDisplay.length) {
    return null
  }

  return (
    <Wrapper attributes={attributes} wrap={wrap}>
      <div className={styles.menu}>
        {itemsToDisplay.map((item) => (
          <React.Fragment key={item.label}>
            <MenuItem
              item={item}
              currentRefinement={currentRefinement}
              onClick={() => refine(item.value as string)}
            />

            {item.items && (
              <div className={styles.subMenu}>
                <CustomHierarchicalMenu
                  items={item.items}
                  refine={refine}
                  currentRefinement={currentRefinement}
                  attributes={attributes}
                  wrap={false}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Wrapper>
  )
}

export default connectHierarchicalMenu(CustomHierarchicalMenu)
